import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { BookText, Plus, Pencil, Trash2, Search, Save, X } from "lucide-react";
import { SectionCard } from "@/components/common/SectionCard";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useUser } from "@/context/UserContext";
import { api } from "@/lib/api";
import { ukToday } from "@/data/taskOptions";

const fmtDateHeader = (iso) => {
  try { return new Date(`${iso}T00:00:00`).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "short", year: "numeric" }); }
  catch { return iso; }
};
const fmtTime = (iso) => { try { return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

// Founder diary accents (author captured server-side from the signed-in user).
const AUTHOR_ACCENT = { Steven: "#1D4ED8", Richard: "#0D9488" };
const AUTHOR_FILTERS = [
  { value: "all", label: "All", accent: "#5A6B8C" },
  { value: "Steven", label: "Steven", accent: AUTHOR_ACCENT.Steven },
  { value: "Richard", label: "Richard", accent: AUTHOR_ACCENT.Richard },
];

export const DiaryPanel = () => {
  const { canWrite } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const authorFilter = (() => {
    const a = searchParams.get("author");
    return AUTHOR_FILTERS.some((f) => f.value === a) ? a : "all";
  })();
  const setAuthorFilter = (v) => {
    const p = new URLSearchParams(searchParams);
    if (!v || v === "all") p.delete("author"); else p.set("author", v);
    setSearchParams(p);
  };
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({ date: ukToday(), entry: "" });
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null); // { id, date, entry } | null
  const [confirmDel, setConfirmDel] = useState(null);
  const [q, setQ] = useState("");
  const [dateFilter, setDateFilter] = useState("");

  const load = () => { setLoading(true); api.diaryEntries().then(setEntries).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const add = async () => {
    const date = (draft.date || "").trim();
    const entry = (draft.entry || "").trim();
    if (!date || !entry) { toast.error("Date and diary entry are required"); return; }
    setSaving(true);
    try { await api.createDiaryEntry({ date, entry }); toast.success("Diary entry saved"); setDraft({ date: ukToday(), entry: "" }); load(); }
    catch { /* interceptor toasts */ } finally { setSaving(false); }
  };

  const saveEdit = async () => {
    const date = (editing.date || "").trim();
    const entry = (editing.entry || "").trim();
    if (!date || !entry) { toast.error("Date and diary entry are required"); return; }
    try { await api.updateDiaryEntry(editing.id, { date, entry }); toast.success("Diary entry updated"); setEditing(null); load(); }
    catch { /* handled */ }
  };

  const remove = async () => { try { await api.deleteDiaryEntry(confirmDel.id); toast.success("Diary entry deleted"); setConfirmDel(null); load(); } catch { /* handled */ } };

  const groups = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const filtered = entries.filter((e) => {
      if (dateFilter && e.date !== dateFilter) return false;
      if (authorFilter !== "all" && e.author !== authorFilter) return false;
      if (needle && !((e.entry || "").toLowerCase().includes(needle) || (e.date || "").includes(needle))) return false;
      return true;
    });
    const byDate = {};
    filtered.forEach((e) => { (byDate[e.date] = byDate[e.date] || []).push(e); });
    return Object.keys(byDate).sort().reverse().map((date) => ({ date, items: byDate[date] }));
  }, [entries, q, dateFilter, authorFilter]);

  return (
    <div className="space-y-5" data-testid="diary-panel">
      {canWrite && (
        <SectionCard testid="diary-add-card" accent="#7C3AED" icon={BookText} title="New diary entry" subtitle="A quick daily log for Founders Journal episodes">
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <label className="text-xs font-semibold text-[#5A6B8C]">Date</label>
              <Input type="date" data-testid="diary-date-input" value={draft.date} onChange={(e) => setDraft((s) => ({ ...s, date: e.target.value }))} className="w-44" />
            </div>
            <Textarea data-testid="diary-entry-input" rows={5} value={draft.entry} onChange={(e) => setDraft((s) => ({ ...s, entry: e.target.value }))} placeholder="What happened today? Jot it all down…" className="resize-y" />
            <div className="flex justify-end">
              <Button data-testid="diary-save-button" onClick={add} disabled={saving} className="rounded-full text-white [background-color:#7C3AED] hover:opacity-90"><Save size={15} /> Save</Button>
            </div>
          </div>
        </SectionCard>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex items-center gap-1 rounded-full border border-[#E4E9F3] bg-white p-1 shadow-sm" data-testid="diary-author-toggle">
          {AUTHOR_FILTERS.map((f) => {
            const active = authorFilter === f.value;
            return (
              <button
                key={f.value}
                type="button"
                data-testid={`diary-author-toggle-${f.value}`}
                onClick={() => setAuthorFilter(f.value)}
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold transition-colors"
                style={active ? { backgroundColor: `${f.accent}1A`, color: f.accent } : { color: "#5A6B8C" }}
              >
                {f.value !== "all" && <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: f.accent }} />}
                {f.label}
              </button>
            );
          })}
        </div>
        <div className="relative min-w-[220px] flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9DAAC6]" />
          <Input data-testid="diary-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search diary…" className="pl-9" />
        </div>
        <Input type="date" data-testid="diary-date-filter" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} className="w-44" />
        {(q || dateFilter || authorFilter !== "all") && (
          <Button variant="ghost" data-testid="diary-clear-filters" onClick={() => { setQ(""); setDateFilter(""); setAuthorFilter("all"); }} className="rounded-full text-[#5A6B8C]"><X size={14} /> Clear</Button>
        )}
      </div>

      {loading ? (
        <p className="py-8 text-center text-sm text-[#9DAAC6]">Loading…</p>
      ) : groups.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-12 text-center" data-testid="diary-empty">
          <BookText size={26} className="text-[#C7D2E8]" />
          <p className="text-sm font-semibold text-[#14245F]">{entries.length === 0 ? "No diary entries yet" : "No entries match your filters"}</p>
          <p className="max-w-sm text-xs text-[#5A6B8C]">{entries.length === 0 && canWrite ? "Capture your first daily log using the box above." : "Try a different founder, search or date."}</p>
        </div>
      ) : (
        <div className="space-y-6" data-testid="diary-list">
          {groups.map((g) => (
            <div key={g.date} data-testid={`diary-group-${g.date}`}>
              <h3 className="mb-2 border-b border-[#EEF2FB] pb-1.5 text-sm font-bold text-[#7C3AED]">{fmtDateHeader(g.date)}</h3>
              <ul className="space-y-3">
                {g.items.map((e) => {
                  const accent = AUTHOR_ACCENT[e.author] || null;
                  const edited = (e.updated_at || "").slice(0, 19) !== (e.created_at || "").slice(0, 19);
                  return (
                  <li key={e.id} data-testid={`diary-entry-${e.id}`} className="group rounded-2xl border border-l-4 border-[#E4E9F3] bg-white p-4 shadow-sm" style={{ borderLeftColor: accent || "#E4E9F3" }}>
                    <div className="flex items-start justify-between gap-3">
                      <p className="whitespace-pre-wrap text-sm leading-relaxed text-[#14245F]">{e.entry}</p>
                      {canWrite && (
                        <div className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                          <Button size="icon" variant="ghost" data-testid={`diary-edit-${e.id}`} onClick={() => setEditing({ id: e.id, date: e.date, entry: e.entry })} className="h-8 w-8 rounded-full text-[#1D4ED8]" title="Edit"><Pencil size={14} /></Button>
                          <Button size="icon" variant="ghost" data-testid={`diary-delete-${e.id}`} onClick={() => setConfirmDel(e)} className="h-8 w-8 rounded-full text-[#C2410C]" title="Delete"><Trash2 size={14} /></Button>
                        </div>
                      )}
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-[#9DAAC6]">
                      {e.author && (
                        <span data-testid={`diary-author-${e.id}`} className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold" style={{ backgroundColor: accent ? `${accent}1A` : "#EEF2FB", color: accent || "#5A6B8C" }}>
                          <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: accent || "#9DAAC6" }} />{e.author}
                        </span>
                      )}
                      <span>{fmtTime(e.created_at)}{edited ? " · edited" : ""}</span>
                    </div>
                  </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="diary-edit-dialog">
          <DialogHeader><DialogTitle>Edit diary entry</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="flex items-center gap-3">
              <label className="text-xs font-semibold text-[#5A6B8C]">Date</label>
              <Input type="date" data-testid="diary-edit-date" value={editing?.date || ""} onChange={(e) => setEditing((s) => ({ ...s, date: e.target.value }))} className="w-44" />
            </div>
            <Textarea data-testid="diary-edit-entry" rows={6} value={editing?.entry || ""} onChange={(e) => setEditing((s) => ({ ...s, entry: e.target.value }))} className="resize-y" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button data-testid="diary-edit-save" onClick={saveEdit} className="text-white [background-color:#7C3AED] hover:opacity-90">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)} title="Delete this diary entry?" softDelete onConfirm={remove} />
    </div>
  );
};
